<script>
import ModalPostComponent from '../../features/modals/ModalPostComponent';

export default {
  name: 'ModalPostView',
  extends: ModalPostComponent,
  data() {
    return {
      rules: {
        title: [
          { required: true, message: 'El título de la publicación es obligatorio.', trigger: 'blur' },
          { min: 3, max: 200, message: 'El título debe tener entre 3 y 200 caracteres.', trigger: 'blur' },
        ],
      },
    };
  },
  computed: {
    isUpdate() {
      return this.formData?.state === 'update' || Boolean(this.formData?.id);
    },
    modalTitle() {
      return this.isUpdate ? 'Editar Publicación' : 'Nueva Publicación';
    },
    modalSubtitle() {
      return this.isUpdate
        ? 'Actualiza el título, la imagen de portada, el contenido enriquecido y la visibilidad'
        : 'Completa la información para registrar y publicar un nuevo artículo en la plataforma';
    },
    hasCustomImage() {
      return Boolean(this.isPostImageTemp || (this.postImageUrl && !this.postImageUrl.includes('add_img.jpg')));
    },
    previewImageUrl() {
      return this.postImageUrl || '/images/courses/add_img.jpg';
    },
  },
  methods: {
    onRemoveCoverImage() {
      this.postImageUrl = '/images/courses/add_img.jpg';
      this.postImageFile = null;
      this.isPostImageTemp = false;
      if (this.formData) {
        this.formData.imageUrl = null;
      }
    },
  },
};
</script>

<template>
  <el-dialog
    v-model="conf.isVisible"
    class="subscription-account-modal post-admin-modal"
    width="900px"
    append-to-body
    :before-close="onCancel"
    :close-on-click-modal="false"
  >
    <!-- Cabecera personalizada del modal -->
    <template #header>
      <div class="student_modal_header">
        <div class="student_modal_header_icon">
          <i class="fas fa-newspaper" />
        </div>
        <div class="student_modal_header_info">
          <h3 class="student_modal_title">
            {{ modalTitle }}
          </h3>
          <p class="student_modal_subtitle">
            {{ modalSubtitle }}
          </p>
        </div>
      </div>
    </template>

    <!-- Formulario principal estandarizado -->
    <el-form
      ref="formData"
      v-loading="conf.isLoading"
      :model="formData"
      :rules="rules"
      status-icon
      :label-position="'top'"
    >
      <!-- Sección 1: Información Principal y Portada -->
      <div class="student_form_section">
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-heading" /> Información Principal y Portada
          </h4>
        </div>

        <!-- Título -->
        <el-form-item
          label="Título de la Publicación"
          prop="title"
        >
          <el-input
            v-model="formData.title"
            placeholder="Ingresa un título descriptivo y atractivo para la publicación..."
            maxlength="200"
            show-word-limit
            clearable
          />
        </el-form-item>

        <!-- Portada -->
        <div class="post_cover_group">
          <label class="el-form-item__label">
            Imagen de Portada
          </label>
          <div class="post_cover_card">
            <div class="post_cover_preview_wrapper">
              <img
                :src="previewImageUrl"
                alt="Portada de la publicación"
                class="post_cover_preview_img"
              >
            </div>
            <div class="post_cover_info">
              <h5 class="cover_info_title">
                Visualización de Portada
              </h5>
              <p class="cover_info_desc">
                Sube una imagen atractiva para el encabezado del artículo. Formatos recomendados: JPG, PNG o WebP (proporción sugerida: 16:9, máx. 5MB).
              </p>

              <div class="cover_actions">
                <el-upload
                  action=""
                  :show-file-list="false"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  :before-upload="onBeforeUploadHandlerPostPicture"
                  class="post_cover_uploader"
                >
                  <el-button
                    type="primary"
                    class="btn_upload_cover"
                  >
                    <i class="fas fa-cloud-upload-alt mr-1" />
                    {{ hasCustomImage ? 'Cambiar Portada' : 'Subir Portada' }}
                  </el-button>
                </el-upload>

                <el-button
                  v-if="hasCustomImage"
                  type="danger"
                  link
                  size="small"
                  class="btn_remove_cover"
                  @click="onRemoveCoverImage"
                >
                  <i class="fas fa-trash-alt mr-1" /> Quitar Imagen
                </el-button>

                <span
                  v-if="isPostImageTemp"
                  class="cover_status_badge is_temp"
                >
                  <i class="fas fa-clock" /> Nueva imagen por guardar
                </span>
                <span
                  v-else-if="hasCustomImage"
                  class="cover_status_badge is_saved"
                >
                  <i class="fas fa-check-circle" /> Portada asignada
                </span>
                <span
                  v-else
                  class="cover_status_badge is_default"
                >
                  <i class="fas fa-image" /> Imagen predeterminada
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sección 2: Contenido del Artículo -->
      <div class="student_form_section">
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-file-alt" /> Contenido de la Publicación
          </h4>
        </div>

        <el-form-item
          label="Cuerpo del Artículo"
          prop="description"
        >
          <el-input
            v-model="formData.description"
            type="textarea"
            class="description-editor"
            :rows="5"
            placeholder="Escribe aquí el contenido completo de la publicación..."
          />
        </el-form-item>

        <div class="editor_hint_box">
          <i class="fas fa-lightbulb" />
          <span>
            Tip: Puedes utilizar el editor enriquecido superior para dar formato a los párrafos, agregar títulos, viñetas, enlaces o fórmulas.
          </span>
        </div>
      </div>

      <!-- Sección 3: Visibilidad y Publicación -->
      <div class="student_form_section">
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-globe-americas" /> Estado y Visibilidad
          </h4>
        </div>

        <div
          class="post_visibility_card"
          :class="{ is_active: formData.isPublished }"
        >
          <div class="visibility_info">
            <div
              class="visibility_icon"
              :class="{ is_active: formData.isPublished }"
            >
              <i :class="formData.isPublished ? 'fas fa-globe-americas' : 'fas fa-lock'" />
            </div>
            <div class="visibility_texts">
              <h5 class="visibility_title">
                {{ formData.isPublished ? 'Publicación Visible en el Portal' : 'Publicación en Borrador (Oculta)' }}
              </h5>
              <p class="visibility_desc">
                {{ formData.isPublished
                  ? 'El artículo se mostrará públicamente a todos los estudiantes registrados en la sección de noticias y novedades.'
                  : 'El artículo solo será visible por el personal administrativo hasta que decidas publicarlo.' }}
              </p>
            </div>
          </div>

          <div class="visibility_controls">
            <el-switch
              v-model="formData.isPublished"
              size="large"
              active-text="Público"
              inactive-text="Borrador"
              active-color="#16a34a"
              inactive-color="#94a3b8"
              :active-value="true"
              :inactive-value="false"
            />
          </div>
        </div>
      </div>
    </el-form>

    <!-- Footer del modal -->
    <template #footer>
      <div class="student_modal_footer">
        <span class="footer_note">
          <i class="fas fa-shield-alt mr-1" />
          Los cambios se guardan de forma segura en la base de datos de la plataforma
        </span>
        <div class="footer_actions">
          <el-button
            class="btn_cancel_student_modal"
            @click="onCancel"
          >
            <i class="fas fa-times mr-1" /> Cancelar
          </el-button>
          <el-button
            type="primary"
            class="btn_submit_student_info"
            :loading="conf.isLoading"
            :disabled="!onCanSaveOrUpdate()"
            @click="onSubmitForm('formData')"
          >
            <i class="fas fa-save mr-2" /> {{ isUpdate ? 'Guardar Cambios' : 'Crear Publicación' }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>
